import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, Radio, message, TimePicker } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import AccentButton from '../components/AccentButton/AccentButton';
import UserTag from '../components/UserTag/UserTag';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import SpecModal from '../components/SpecModal/SpecModal';
import CalendarOverlay from '../components/CalendarOverlay/CalendarOverlay';
import EditingBar from '../components/EditingBar/EditingBar';
import { CalendarIcon, ChatIcon } from '../components/icons';
import { radius } from '../theme/tokens/radius';
import { useBands, useVenues, useShow } from '../api/queries';
import { create_show, update_show, delete_show, Venue } from '../api';
import { useAuth } from '../contexts/AuthContext';
import './AddShow.css';
import './EditProfile.css';

const AddShow: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const isEditMode = (location.state as any)?.editMode === true;
    const editShowId = (location.state as any)?.showId as string | undefined;
    const preselectedBandId = (location.state as any)?.bandId as string | undefined;

    // Load bands and venues from API
    const { data: bands = [], isLoading: bandsLoading } = useBands();
    const { data: venues = [], isLoading: venuesLoading } = useVenues();

    // Load existing show data when in edit mode
    const { data: existingShow, isLoading: showLoading } = useShow(isEditMode ? editShowId : undefined);

    // Check if user is admin, musician, or has specific band roles
    const isAdmin = user?.claims?.roles?.includes('admin') ?? false;
    const isMusician = user?.claims?.roles?.includes('musician') ?? false;
    const userBandIds = useMemo(() => {
        if (!user?.claims?.bandRoles) return [];
        return Object.keys(user.claims.bandRoles);
    }, [user?.claims?.bandRoles]);

    // Filter bands based on user permissions
    // Admins and musicians see all bands, contributors only see their bands
    const allowedBands = useMemo(() => {
        if (isAdmin || isMusician) return bands;
        if (userBandIds.length === 0) return [];
        return bands.filter(band => userBandIds.includes(band.id));
    }, [bands, isAdmin, isMusician, userBandIds]);

    // Only use preselected band if user has access to it
    const validPreselectedBandId = useMemo(() => {
        if (!preselectedBandId) return null;
        if (isAdmin) return preselectedBandId;
        return userBandIds.includes(preselectedBandId) ? preselectedBandId : null;
    }, [preselectedBandId, isAdmin, userBandIds]);

    const [selectedBandId, setSelectedBandId] = useState<string | null>(validPreselectedBandId);
    const [bandSearch, setBandSearch] = useState('');
    const [venueSearch, setVenueSearch] = useState('');
    const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
    const [date, setDate] = useState('');
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [time, setTime] = useState<dayjs.Dayjs | null>(null);
    const [description, setDescription] = useState('');
    const [address, setAddress] = useState('');
    const [abandonModalOpen, setAbandonModalOpen] = useState(false);
    const [deleteShowModalOpen, setDeleteShowModalOpen] = useState(false);
    const [bandInfoOpen, setBandInfoOpen] = useState(false);
    const [venueInfoOpen, setVenueInfoOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [formInitialized, setFormInitialized] = useState(false);

    // Pre-fill form when existing show data loads (edit mode)
    useEffect(() => {
        if (isEditMode && existingShow && !formInitialized) {
            setSelectedBandId(existingShow.band_id);
            setSelectedVenueId(existingShow.venue_id || null);
            setAddress(existingShow.address || '');
            setDescription(existingShow.description || '');

            // Parse date from YYYY-MM-DD to MM/DD/YYYY
            if (existingShow.date) {
                const parsedDate = dayjs(existingShow.date);
                if (parsedDate.isValid()) {
                    setDate(parsedDate.format('MM/DD/YYYY'));
                }
            }

            // Parse time string (e.g., "8:00 PM")
            if (existingShow.time) {
                const parsedTime = dayjs(existingShow.time, 'h:mm A');
                if (parsedTime.isValid()) {
                    setTime(parsedTime);
                }
            }

            setFormInitialized(true);
        }
    }, [isEditMode, existingShow, formInitialized]);

    // Filter bands by search (from allowed bands only)
    const filteredBands = allowedBands.filter((b) =>
        b.name.toLowerCase().includes(bandSearch.trim().toLowerCase())
    );

    // Filter venues by search
    const filteredVenues = venues.filter((v) =>
        v.name.toLowerCase().includes(venueSearch.trim().toLowerCase())
    );

    const handleVenueSelect = (venue: Venue) => {
        setSelectedVenueId(venue.id);
        setAddress(venue.address || '');
    };

    const selectedBand = allowedBands.find(b => b.id === selectedBandId);
    const selectedVenue = venues.find(v => v.id === selectedVenueId);
    const canSubmit = selectedBandId && selectedBand && (selectedVenueId || address.trim()) && date.trim() && time && !creating;

    const handleSubmit = async () => {
        if (!canSubmit || !selectedBand || !time) return;

        // Convert date from MM/DD/YYYY to ISO format YYYY-MM-DD
        const parsedDate = dayjs(date, 'MM/DD/YYYY');
        if (!parsedDate.isValid()) {
            message.error('Please enter a valid date');
            return;
        }

        setCreating(true);
        try {
            const showData = {
                band_id: selectedBand.id,
                band_name: selectedBand.name,
                venue_id: selectedVenue?.id,
                venue_name: selectedVenue?.name,
                address: address.trim() || selectedVenue?.address || 'TBA',
                latitude: selectedVenue?.latitude,
                longitude: selectedVenue?.longitude,
                date: parsedDate.format('YYYY-MM-DD'),
                time: time.format('h:mm A'),
                description: description.trim() || undefined,
            };

            if (isEditMode && editShowId) {
                // Update existing show
                await update_show(editShowId, showData);

                // Invalidate queries to refetch fresh data
                await queryClient.invalidateQueries({ queryKey: ['show', editShowId] });
                await queryClient.invalidateQueries({ queryKey: ['shows'] });
                await queryClient.invalidateQueries({ queryKey: ['band', selectedBand.id, 'shows'] });

                message.success('Show updated successfully!');
                navigate(`/shows/${editShowId}`);
            } else {
                // Create new show
                const newShow = await create_show(showData);

                // Invalidate queries to refetch fresh data
                await queryClient.invalidateQueries({ queryKey: ['shows'] });
                await queryClient.invalidateQueries({ queryKey: ['band', selectedBand.id, 'shows'] });

                message.success('Show created successfully!');
                navigate(`/shows/${newShow.id}`);
            }
        } catch (error: any) {
            message.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} show`);
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async () => {
        if (!editShowId) return;

        setDeleting(true);
        try {
            await delete_show(editShowId);

            // Invalidate queries
            await queryClient.invalidateQueries({ queryKey: ['shows'] });
            if (selectedBand) {
                await queryClient.invalidateQueries({ queryKey: ['band', selectedBand.id, 'shows'] });
            }

            message.success('Show deleted successfully!');
            setDeleteShowModalOpen(false);
            navigate('/home');
        } catch (error: any) {
            message.error(error.message || 'Failed to delete show');
            setDeleting(false);
        }
    };

    const handleAddBandConfirm = () => {
        setAbandonModalOpen(false);
        navigate('/add-band');
    };

    // Show loading state while fetching existing show in edit mode
    if (isEditMode && showLoading) {
        return (
            <div className="addshow-page">
                <div className="addshow-content">
                    <div className="addshow-loading">Loading show...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="addshow-page">
            {isEditMode && (
                <EditingBar
                    icon={<CalendarIcon size="m" tone="white" />}
                    title="Editing Show"
                    onCancel={() => navigate(-1)}
                    onSave={handleSubmit}
                />
            )}
            <div className={`addshow-content${isEditMode ? ' addshow-content--editing' : ''}`}>
                {!isEditMode && <h1 className="addshow-title">Add Show</h1>}

                <div className="addshow-form">
                    {/* Band Selection */}
                    <div className="addshow-field addshow-field--bands">
                        <div className="addshow-label">
                            {isEditMode ? 'Band' : 'Select Band'}
                            <UserTag type="band" onClick={() => setBandInfoOpen(true)} />
                        </div>
                        {bandsLoading ? (
                            <div className="addshow-loading">Loading bands...</div>
                        ) : !isAdmin && allowedBands.length === 0 ? (
                            <div className="addshow-empty-list">
                                You don't have permission to add shows for any bands.
                                Contact an admin to become a band contributor.
                            </div>
                        ) : isEditMode || validPreselectedBandId ? (
                            selectedBand ? (
                                <div className="addshow-band-item addshow-band-item--locked">
                                    {selectedBand.photo_url ? (
                                        <img
                                            className="addshow-band-avatar"
                                            src={selectedBand.photo_url}
                                            alt={selectedBand.name}
                                        />
                                    ) : (
                                        <div className="addshow-band-avatar addshow-band-avatar--placeholder" />
                                    )}
                                    <span className="addshow-band-name">{selectedBand.name}</span>
                                </div>
                            ) : null
                        ) : (
                            <>
                                <Input
                                    placeholder="Search bands..."
                                    value={bandSearch}
                                    onChange={(e) => setBandSearch(e.target.value)}
                                    className="addshow-search-input"
                                />
                                <div className="addshow-band-list addshow-band-list--scrollable">
                                    {filteredBands.length > 0 ? (
                                        filteredBands.map((band) => (
                                            <button
                                                key={band.id}
                                                className={`addshow-band-item${selectedBandId === band.id ? ' addshow-band-item--selected' : ''}`}
                                                onClick={() => setSelectedBandId(band.id)}
                                            >
                                                {band.photo_url ? (
                                                    <img
                                                        className="addshow-band-avatar"
                                                        src={band.photo_url}
                                                        alt={band.name}
                                                    />
                                                ) : (
                                                    <div className="addshow-band-avatar addshow-band-avatar--placeholder" />
                                                )}
                                                <span className="addshow-band-name">{band.name}</span>
                                                <Radio
                                                    checked={selectedBandId === band.id}
                                                    className="addshow-band-radio"
                                                />
                                            </button>
                                        ))
                                    ) : (
                                        <div className="addshow-empty-list">No bands found</div>
                                    )}
                                </div>
{(isAdmin || isMusician) && (
                                    <Button
                                        type="primary"
                                        size="large"
                                        shape="round"
                                        icon={<ChatIcon size="s" tone="black" />}
                                        className="addshow-add-band-btn"
                                        onClick={() => setAbandonModalOpen(true)}
                                    >
                                        Add Band
                                    </Button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Venue */}
                    <div className="addshow-field addshow-field--venues">
                        <div className="addshow-label">
                            Venue
                            <UserTag type="venue" onClick={() => setVenueInfoOpen(true)} />
                        </div>
                        {venuesLoading ? (
                            <div className="addshow-loading">Loading venues...</div>
                        ) : (
                            <>
                                <Input
                                    placeholder="Search venues..."
                                    value={venueSearch}
                                    onChange={(e) => setVenueSearch(e.target.value)}
                                    className="addshow-search-input"
                                />
                                <div className="addshow-venue-list addshow-venue-list--scrollable">
                                    {filteredVenues.length > 0 ? (
                                        filteredVenues.map((venue) => (
                                            <button
                                                key={venue.id}
                                                className={`addshow-venue-item${selectedVenueId === venue.id ? ' addshow-venue-item--selected' : ''}`}
                                                onClick={() => handleVenueSelect(venue)}
                                            >
                                                <div className="addshow-venue-info">
                                                    <span className="addshow-venue-name">{venue.name}</span>
                                                    {venue.address && (
                                                        <span className="addshow-venue-address">{venue.address}</span>
                                                    )}
                                                </div>
                                                <Radio
                                                    checked={selectedVenueId === venue.id}
                                                    className="addshow-venue-radio"
                                                />
                                            </button>
                                        ))
                                    ) : (
                                        <div className="addshow-empty-list">No venues found</div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Address */}
                    <div className="addshow-field">
                        <div className="addshow-label">
                            Address
                            {selectedVenue && <span className="addshow-label-optional">Auto-filled</span>}
                        </div>
                        <Input
                            placeholder="123 Main St, Tulsa, OK"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                        />
                    </div>

                    {/* Date */}
                    <div className="addshow-field">
                        <label className="addshow-label">Date</label>
                        <Input
                            placeholder="mm/dd/yyyy"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            suffix={
                                <span
                                    style={{ cursor: 'pointer', display: 'flex' }}
                                    onClick={() => setCalendarOpen(true)}
                                >
                                    <CalendarIcon size="s" tone="lightGrey" />
                                </span>
                            }
                        />
                    </div>

                    {/* Time */}
                    <div className="addshow-field">
                        <label className="addshow-label">Time</label>
                        <TimePicker
                            value={time}
                            onChange={(value) => setTime(value)}
                            format="h:mm A"
                            use12Hours
                            placeholder="Select time"
                            className="addshow-time-picker"
                            minuteStep={15}
                            needConfirm={false}
                        />
                    </div>

                    {/* Description */}
                    <div className="addshow-field">
                        <div className="addshow-label">
                            Description
                            <span className="addshow-label-optional">Optional</span>
                        </div>
                        <Input.TextArea
                            placeholder="Residency name, show description, etc."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            styles={{
                                textarea: { borderRadius: radius.m, resize: 'none', height: 60 },
                            }}
                        />
                    </div>

                    {/* Submit */}
                    <AccentButton
                        size="large"
                        block
                        shape="round"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        loading={creating}
                    >
                        {isEditMode ? 'Save Show' : 'Add Show'}
                    </AccentButton>

                    {isEditMode && (
                        <button
                            className="edit-profile-danger-link"
                            onClick={() => setDeleteShowModalOpen(true)}
                        >
                            Delete Show
                        </button>
                    )}
                </div>
            </div>

            {/* Calendar Overlay */}
            <CalendarOverlay
                open={calendarOpen}
                onClose={() => setCalendarOpen(false)}
                value={date ? dayjs(date, 'MM/DD/YYYY') : undefined}
                onSelect={(d) => setDate(d.format('MM/DD/YYYY'))}
            />

            {/* Abandon Show Modal */}
            <SpecModal
                open={abandonModalOpen}
                onCancel={() => setAbandonModalOpen(false)}
                title="Abandon Show"
                footer={null}
                closable={false}
                centered
                width={340}
            >
                <p className="addshow-modal-body">
                    Discard adding show to add a band
                </p>
                <div className="addshow-modal-actions">
                    <Button
                        size="large"
                        shape="round"
                        onClick={() => setAbandonModalOpen(false)}
                    >
                        Back
                    </Button>
                    <AccentButton
                        size="large"
                        shape="round"
                        onClick={handleAddBandConfirm}
                    >
                        Add Band
                    </AccentButton>
                </div>
            </SpecModal>

            <UserTypeInfo type="band" open={bandInfoOpen} onClose={() => setBandInfoOpen(false)} />
            <UserTypeInfo type="venue" open={venueInfoOpen} onClose={() => setVenueInfoOpen(false)} />

            {/* Delete Show Modal (edit mode only) */}
            {isEditMode && (
                <SpecModal
                    open={deleteShowModalOpen}
                    onCancel={() => setDeleteShowModalOpen(false)}
                    title="Delete Show"
                    footer={null}
                    closable={false}
                    centered
                    width={340}
                >
                    <p className="addshow-modal-body">
                        Are you sure you want to delete this show? This cannot be undone.
                    </p>
                    <div className="addshow-modal-actions">
                        <Button
                            size="large"
                            shape="round"
                            onClick={() => setDeleteShowModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="large"
                            shape="round"
                            danger
                            type="primary"
                            loading={deleting}
                            onClick={handleDelete}
                        >
                            Delete
                        </Button>
                    </div>
                </SpecModal>
            )}
        </div>
    );
};

export default AddShow;
